import { Models, Types, Assets, Services, Utils } from "@sendum/core";

const hiddenYAxis = {
	min: 0,
	max: 1.1,
	startOnTick: false,
	endOnTick: false,
	gridLineWidth: 0,
	title: {
		text: null,
	},
	id: "hidden",
	labels: {
		enabled: false,
	},
	opposite: true,
};

export const doorOpenMarker = `url(${Assets.Sensors.DoorOpenChart})`;
export const white = `url(${Assets.WhiteChart})`;

export const getYAxis = (device: Models.Device, sensors: string[]): any[] => {
	const config: Map<string, Types.TDeviceAttribute> = device.getAttributesConfig();
	const yAxisTypes = [...config.keys()].filter(key => sensors.includes(key)).map(key => config.get(key)?.yAxisId || "");

	if (sensors.length === 0) {
		return [hiddenYAxis];
	}

	const uniqueYaxis = new Set(yAxisTypes);
	if (Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart()) {
		uniqueYaxis.add("hidden");
	}

	return [...uniqueYaxis].map(type => {
		switch (type) {
			case "Temperature_C": {
				return getDefaultYAxis("°C", "", "Temperature_C");
			}
			case "Temperature": {
				return getDefaultYAxis("°C", "", "Temperature");
			}
			case "Humidity": {
				const axis = getDefaultYAxis("%RH", "", "Humidity");
				axis.min = 0;
				axis.max = 100;

				return axis;
			}
			case "Battery": {
				const axis = getDefaultYAxis("%", "", "Battery");
				axis.min = 0;
				axis.max = 100;

				return axis;
			}
			case "hidden": {
				return hiddenYAxis;
			}
			default: {
				return getDefaultYAxis("", type, type);
			}
		}
	});
};

export const getUnit = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["unit"];
		}
	}

	return "";
};

export const getSeriesName = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["displayName"];
		}
	}

	return sensorName;
};

export const getYAxisId = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);

		if (config) {
			return config["yAxisId"];
		}

		return "hidden";
	}

	return "";
};

export const getChartType = (device: Models.Device, sensorName?: string): string => {
	if (device && sensorName) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["chartType"];
		}
	}

	return "line";
};

export const getChartColor = (device: Models.Device, sensorName?: string): string => {
	if (device && sensorName) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return readCSSColor(config["color"]).trim();
		}
	}

	return "#009684";
};

export const getSensorDataType = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["type"];
		}
	}

	return "number";
};

export const getSensorBooleanValue = (device: Models.Device, sensorName: string, value: boolean): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return value ? config["trueValue"] : config["falseValue"];
		}
	}

	return +value + "";
};

export const getHistoryBySensorName = (device: Models.Device, history: Map<string, Types.TProperty[]>, sensorName: string): number[][] => {
	if (!device?.id) {
		return [];
	}
	const deviceHistory = history.get(`${device.id}#${device.shipment.id}#${sensorName}`);

	if (!deviceHistory) {
		return [];
	}

	return deviceHistory
		.map(_ => (_ && _.epoch ? [_.epoch, +_["value"]] : []))
		.filter(_ => _.length)
		.sort((a, b) => ((a[0] && b[0] && a[0] > b[0]) ? 1 : -1));
};

export const getDefaultYAxis = (unit: string, seriesName: string, yAxisId: string): any => ({
	unit: ` ${unit} `,
	title: {
		align: "high",
		textAlign: "left",
		text: seriesName + " " + unit,
		rotation: 0,
		x: 0,
		y: -5,
	},
	id: yAxisId,
	showEmpty: true,
	opposite: false,
	labels: {
		align: "left",
	},
});

export const readCSSColor = (name: string): string => {
	const bodyStyles = window.getComputedStyle(document.body);

	return bodyStyles.getPropertyValue(name);
};

export const getDefaultHighchartsConfig = (showMapPopup: (e: any) => void) => ({
	title: {
		text: "SENSOR CHART",
	},
	exporting: {
		buttons: {
			contextButton: {
				menuItems: ["printChart", "separator", "downloadPNG"],// , "downloadPDF" // "printChart" not working in highchart 8.1.0 regression bug #13489. should be fixed in next release.
			},
		},
	},
	chart: {
		animation: false,
		type: "line",
		zoomType: "x",
		events: {
			afterPrint() {
				window.dispatchEvent(new Event("resize"));
			},
		},
	},
	navigator: {
		enabled: true,
		series: [
			{
				fillOpacity: 0.05,
			},
		],
		height: 10,
		margin: 0,
		xAxis: {
			showEmpty: true,
			labels: {
				y: 12,
				align: "center",
				style: {
					fontSize: "10px",
				},
			},
			tickPixelInterval: 90,
			startOnTick: true,
			minRange: Utils.time.MILLISECONDS_OF_HOUR,
			dateTimeLabelFormats: {
				second: "%e %b",
				minute: "%e %b",
				hour: "%e %b",
				day: "%e %b",
				week: "%e %b",
				month: "%e %b",
				year: "%e %b",
			},
		},
	},
	scrollbar: {
		enabled: false,
	},
	plotOptions: {
		series: {
			allowPointSelect: true,
			showInLegend: true,
			point: {
				events: {
					click(e: any) {
              	let time = this["x"];
              	if (e.point.series.hasGroupedData) {
              		const index = e.point.dataGroup.start;
              		time = e.point.series.xData[index];
              	}
              	showMapPopup(time);
              	const { xAxis } = e.point.series;
              	xAxis.plotLinesAndBands.forEach((p: any) => {
              		if (!p.id) {
              			p.destroy();
              		}
              	});
              	xAxis.addPlotLine({
              		value: this["x"],
              		width: 3,
              		color: readCSSColor("--sw-storm-gray-color"),
              		dashStyle: "ShortDot",
              		zIndex: 1,
              	});
					},
				},
			},
		},
	},
	tooltip: {
		split: false,
		shared: true,
		valueDecimals: 2,
	},
	xAxis: {
		id: "mainXAxis",
		type: "datetime",
		ordinal: false,
		crosshair: true,
		showEmpty: true,
		events: {},
		minRange: Utils.time.MILLISECONDS_OF_HOUR,
		dateTimeLabelFormats: {
			second: "%H:%M <br /> %e %b",
			minute: "%H:%M <br /> %e %b",
			hour: "%H:%M <br /> %e %b",
			day: "%H:%M <br /> %e %b",
			week: "%e %b",
			month: "%e %b",
			year: "%e %b",
		},
		tickPixelInterval: 90,
		labels: {
			style: {
				fontSize: "10px",
			},
		},
	},
	legend: {
		symbolWidth: 9,
		symbolHeight: 14,
		enabled: true,
		maxHeight: 80,
		itemDistance: 5,
	},
	yAxis: [],
	rangeSelector: {
		buttons: [
			{
				type: "hour",
				count: 1,
				text: "1h",
			},
			{
				type: "day",
				count: 1,
				text: "1d",
			},
			{
				type: "month",
				count: 1,
				text: "1m",
			},
			{
				type: "year",
				count: 1,
				text: "1y",
			},
			{
				type: "all",
				text: "All",
			},
		],
		inputEnabled: false,
		selected: 4, // all
	},
	responsive: {
		rules: [
			{
				condition: {
					maxWidth: 800,
				},
				chartOptions: {
					rangeSelector: {
						inputEnabled: false,
					},
				},
			},
		],
	},
});
